import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { AuthState } from '../types/auth';
import { loadServerConfig } from '../config/env';
import { getValidToken } from '../utils/tokenValidation';
import { initiateLogin } from '../utils/oauth';
import { ErrorDisplay } from '../components/ErrorDisplay.tsx';
import { LoadingDisplay } from '../components/LoadingDisplay.tsx';

interface AuthContextType {
  getValidToken: () => Promise<string | null>;
  login: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

function AutoLogin({ onLogin }: { onLogin: () => Promise<void> }) {
  useEffect(() => {
    onLogin().catch(console.error);
  }, [onLogin]);

  return <LoadingDisplay message="Redirecting..." subtitle="Logging in..." />;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    token: null,
    isLoading: true,
    error: null,
  });

  const getValidTokenCallback = useCallback(async (): Promise<
    string | null
  > => {
    return getValidToken();
  }, []);

  const loginCallback = useCallback(async (): Promise<void> => {
    await initiateLogin();
  }, []);

  useEffect(() => {
    async function initializeAuth() {
      try {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

        await loadServerConfig();
        const token = await getValidToken();

        if (!token) {
          setAuthState({
            token: null,
            isLoading: false,
            error: null, // No error - user just needs to log in
          });
          return;
        }

        setAuthState({
          token,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        setAuthState({
          token: null,
          isLoading: false,
          error: `Authentication failed: ${error instanceof Error ? error.message : String(error)}`,
        });
      }
    }

    initializeAuth();
  }, []);

  const contextValue = useMemo(
    () => ({
      getValidToken: getValidTokenCallback,
      login: loginCallback,
    }),
    [getValidTokenCallback, loginCallback]
  );

  const isAuthenticated = !!authState.token && !authState.error;

  if (authState.isLoading) {
    return <LoadingDisplay message="Loading..." subtitle="Authenticating..." />;
  }

  // Show login prompt when not authenticated
  if (!isAuthenticated && !authState.error) {
    return <AutoLogin onLogin={loginCallback} />;
  }

  // Show error if authentication failed
  if (authState.error) {
    return (
      <ErrorDisplay message={`Authentication Error: ${authState.error}`} />
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
